import { decodePathShape, encodePathShape, PathCommand } from './protobuf_util';

const itemIsScribbleOrHighlight = (object) =>
  object.type === 'path' && object.itemType !== 'Line';

/**
 * Transforms fabric.js path array format to Protobuf PathCommand format.
 * From: [['M', x, y], ['L', x, y]]
 * To: [{type: 'M', values: [x, y]}, {type: 'L', values: [x, y]}]
 */
const transformPathToProtobufFormat = (path: any[]): Array<PathCommand> => {
  return path.map((pathArray: any) => ({
    type: pathArray[0], // First element is the command type (M, L, Q, etc.)
    values: Array.isArray(pathArray) ? pathArray.slice(1) : [] // Rest are the coordinate values
  }));
};

/**
 * Transforms Protobuf PathCommand format back to fabric.js path array format.
 * From: [{type: 'M', values: [x, y]}, {type: 'L', values: [x, y]}]
 * To: [['M', x, y], ['L', x, y]]
 */
const transformPathFromProtobufFormat = (
  pathCommands: Array<{ type: string; values: number[] }>
): any[][] => {
  return pathCommands.map((cmd) => [cmd.type, ...(cmd.values || [])]);
};

/**
 * Encodes a scribble or highlight fabric object to a Protobuf buffer.
 * @param fabricObject The fabric object to encode
 * @returns A Uint8Array buffer containing the encoded protobuf data
 * @throws Error if protobuf is not loaded or encoding fails
 */
export const encodeObjectIfNeeded = (fabricObject) => {
  if (!itemIsScribbleOrHighlight(fabricObject)) {
    return fabricObject;
  }

  const path = (fabricObject as any)?.path;
  if (!path || !Array.isArray(path)) {
    throw new Error('Invalid path data in fabric object');
  }

  // Transform path array format to PathCommand objects
  const transformedPath = transformPathToProtobufFormat(path);

  // Encode to buffer
  const binaryBuffer = encodePathShape({
    left: fabricObject.left,
    top: fabricObject.top,
    width: fabricObject.width,
    height: fabricObject.height,
    stroke: fabricObject.stroke,
    angle: fabricObject.angle,
    scaleX: fabricObject.scaleX,
    scaleY: fabricObject.scaleY,
    skewX: fabricObject.skewX,
    skewY: fabricObject.skewY,
    flipY: fabricObject.flipY,
    flipX: fabricObject.flipX,
    locked: fabricObject.locked,
    stroke_width: fabricObject.strokeWidth,
    path: transformedPath
  });

  return {
    ...getTopLeveLPropertiesFromFabricObject(fabricObject),
    binaryBuffer
  };
};

const decodeScribble = (fabricObject) => {
  if (!fabricObject.binaryBuffer) {
    // Protobuf not loaded yet, return as-is (will be decoded on next emission)
    throw new Error('No buffer to decode');
  }

  try {
    const decoded = decodePathShape(fabricObject.binaryBuffer) as any;

    // Transform PathCommand objects back to fabric.js path array format
    const fabricPath = decoded.path
      ? transformPathFromProtobufFormat(decoded.path)
      : decoded.path;

    // Return the decoded object with the transformed path
    return {
      ...decoded,
      path: fabricPath,
      ...getTopLeveLPropertiesFromFabricObject(fabricObject),
      strokeWidth: decoded.stroke_width,
      // The values that are constants for all scribbles and highlights
      type: 'path',
      version: '5.3.0',
      fill: null,
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
      strokeMiterLimit: 10,
      erasable: true,
      ScribbleType: 'draw',
      excludeFromExport: false,
      hasControls: true,
      skipRemoteAddition: true
    };
  } catch (error) {
    throw new Error('Decoding failed');
  }
};

// Those properties shouldn't be encoded so since they are used a lot when processing the items
// This is, so we don't have to decode the items frequently and cause performance bottlenecks
const getTopLeveLPropertiesFromFabricObject = (fabricObject) => {
  return {
    uid: fabricObject.uid,
    vuid: fabricObject.vuid,
    boardUid: fabricObject.boardUid,
    userId: fabricObject.userId,
    order: fabricObject.order
  };
};

export const decodeFabricItems = (fabricItems: any[]): any[] => {
  return fabricItems.map((item) =>
    item.binaryBuffer ? decodeScribble(item) : item
  );
};
